import { defineComponent as _defineComponent } from 'vue'
import { RetentionItem } from '@/@types/RetentionItem';
import MyTable from '@/components/layout/MyTable/MyTable.vue';
import MyTableCell from '@/components/layout/MyTable/MyTableCell.vue';
import { PERSONAL_WORKSPACE_TITLE } from '@/constants/SharedConsts';
import logger from '@/other/Logger';
import router from '@/router';
import { ARTICLE_EDIT, DRAFT_EDIT, HOME, RETENTION } from '@/routerNames';
import apiService from '@/services/ApiService';
import { ORIGIN_QUERY_PARAM } from '@/services/NavigationService';
import notificationService from '@/services/NotificationService';
import store from '@/store';
import { dispatchGetRetentionCount } from '@/store/dispatchers/uiDispatchers';
import { ref, computed, onMounted, watch } from 'vue';
import {
  RETENTION_STATUS_HINTS,
  RETENTION_STATUS_LABELS,
  RetentionStatus,
  resolveRetentionItemStatus,
} from '@/other/retentionStatus';
import { Loading } from 'element-ui';
import moment from 'moment';
import { debounce } from 'lodash';


export default /*#__PURE__*/_defineComponent({
  __name: 'RetentionPage',
  setup(__props) {

const isRetentionEnabled = computed(() => store.state.company?.retention?.enabled ?? false);

const allItems = ref<RetentionItem[]>([]);
const isLoaded = ref(false);
const isLoading = ref(false);

const selectedItems = ref<string[]>([]);

const isSelectAllMode = ref(false);

const page = ref(1);
const itemPerPage = ref(20);
const totalCount = ref(0);

const sortByColumn = ref<string | null>(null);
const sortDirection = ref<number>(1); // 1 for asc, -1 for desc

// Filters
const searchQuery = ref('');
const searchWorkspace = ref('');
const searchAuthor = ref('');
const searchStatus = ref('');

// Filter data
const workspaces = ref<{ id: string; title: string }[]>([]);
const authors = ref<string[]>([]);

const showDeleteModal = ref(false);
const deleteConfirmMessage = ref('');
const pendingDeleteIds = ref<string[]>([]);

const showRetainModal = ref(false);
const retainConfirmMessage = ref('');
const pendingRetainItems = ref<RetentionItem[]>([]);

// Filter values are the backend `status` query values; order matches the badge severity
const statusOptions = [
  { value: RetentionStatus.EXPIRED, label: RETENTION_STATUS_LABELS[RetentionStatus.EXPIRED] },
  { value: RetentionStatus.RETAINED, label: RETENTION_STATUS_LABELS[RetentionStatus.RETAINED] },
  {
    value: RetentionStatus.LONG_TERM_RETAINED,
    label: RETENTION_STATUS_LABELS[RetentionStatus.LONG_TERM_RETAINED],
  },
];

const tableColumns = [
  { label: '' }, // Checkbox
  { label: 'TITLE' },
  { label: 'WORKSPACE' },
  { label: 'CREATED BY' },
  { label: 'CREATION TIME', sortBy: ['createdAt'] },
  { label: 'AUTO-DELETED IN', sortBy: ['autoDeleteAt'] },
  { label: 'STATUS' },
  { label: '' },
];

const totalPages = computed(() => {
  return Math.ceil(totalCount.value / itemPerPage.value) || 1;
});

const sortedItems = computed(() => {
  // Backend performs global sorting; keep this as a passthrough so selection logic continues to use the displayed rows.
  return [...allItems.value];
});

const displaySelectedCount = computed(() => {
  return selectedItems.value.length;
});

const hasNonRetainedSelected = computed(() => {
  return selectedItems.value.some((id) => {
    const item = allItems.value.find((i) => i.id === id);
    return item && !isRetained(item);
  });
});

const expiredSelectedCount = computed(() => {
  return selectedItems.value.filter((id) => {
    const item = allItems.value.find((i) => i.id === id);
    return item && !isRetained(item);
  }).length;
});

const workspaceTitle = (title: string) => (title?.indexOf('PW_') == 0 ? PERSONAL_WORKSPACE_TITLE : title);

// Which of the three badges a row gets: workspace-enforced (SNAP-2246) wins over the own flag (SNAP-2265)
const retentionStatus = (item: RetentionItem): RetentionStatus => resolveRetentionItemStatus(item);

// Protected from deletion either individually or by the workspace setting
const isRetained = (item: RetentionItem): boolean => retentionStatus(item) !== RetentionStatus.EXPIRED;

const calculateAutoDeleteAt = (item: RetentionItem): Date | null => {
  if (isRetained(item) || !item.hardDeleteCountdownStart) return null;

  const hardDeleteDays = store.state.company?.retention?.hardDeleteDays || 30;
  const countdownStart = moment(item.hardDeleteCountdownStart);
  const hardDeleteDate = countdownStart.clone().add(hardDeleteDays, 'days');

  // Align to next 1:00 AM UTC Job
  // The deletion job runs at 1 AM UTC. We need to find the first 1 AM UTC *after* the hard delete date.
  const deletionSchedule = hardDeleteDate.clone().utc();

  // If the hard delete time is already past 1 AM UTC, the job will pick it up the NEXT day.
  // If it is before 1 AM UTC, the job will pick it up the SAME day (at 1 AM).
  if (deletionSchedule.hour() >= 1 || (deletionSchedule.hour() === 1 && deletionSchedule.minute() > 0)) {
    deletionSchedule.add(1, 'days');
  }

  // Set explicit job execution time
  deletionSchedule.set({ hour: 1, minute: 0, second: 0, millisecond: 0 });

  return deletionSchedule.toDate();
};

const calculateDaysLeft = (item: RetentionItem): string => {
  if (isRetained(item)) return '-';

  const autoDeleteAt = calculateAutoDeleteAt(item);
  if (!autoDeleteAt) return '-';

  const now = moment();
  const deleteDate = moment(autoDeleteAt);

  if (deleteDate.isSameOrBefore(now)) {
    return '0 Hours';
  }

  const hoursLeft = deleteDate.diff(now, 'hours');

  if (hoursLeft < 24) {
    // If less than 24 hours, display in Hours
    return `${hoursLeft} Hour${hoursLeft !== 1 ? 's' : ''}`;
  } else {
    // If 24 hours or more, display in Days
    const daysLeft = deleteDate.diff(now, 'days');
    return `${daysLeft} Day${daysLeft !== 1 ? 's' : ''}`;
  }
};

const formatCreationDate = (timestamp: number | null | undefined): string => {
  if (!timestamp) return '-';
  return moment(timestamp).format('MM/D/YYYY [at] h:mm A');
};

const handleSingleSelection = () => {
  const currentPageIds = sortedItems.value.map((i) => i.id);
  const allCurrentSelected = currentPageIds.every((id) => selectedItems.value.includes(id));
  isSelectAllMode.value = currentPageIds.length > 0 && allCurrentSelected;
};

const handleSelectAllToggle = () => {
  if (isSelectAllMode.value) {
    // Select all items on current page
    selectedItems.value = sortedItems.value.map((i) => i.id);
  } else {
    // Deselect all items
    selectedItems.value = [];
  }
};

watch(page, () => {
  // Clear selection when changing pages
  selectedItems.value = [];
  isSelectAllMode.value = false;
});

const loadFilterData = async () => {
  try {
    const res = await apiService.retention.getFiltersData();
    const data = res.data.filters;
    workspaces.value = [...new Map(data.map((item: any) => [item.workspace.id, item.workspace])).values()].filter(
      (w: any) => w?.title?.length
    );
    authors.value = [...new Set(data.map((item: any) => item.author).filter(Boolean))].sort();
  } catch (error) {
    logger.error('Failed to load filter data', error);
  }
};

const loadItems = async () => {
  const loader = Loading.service({ lock: true, text: 'Loading retention data' });

  try {
    const isFetchTotalCount = totalCount.value ? 0 : 1;
    const res = await apiService.retention.listExpiredItems(
      page.value,
      isFetchTotalCount,
      {
        q: searchQuery.value.length ? searchQuery.value : undefined,
        workspace: searchWorkspace.value.length ? searchWorkspace.value : undefined,
        author: searchAuthor.value.length ? searchAuthor.value : undefined,
        status: searchStatus.value.length ? searchStatus.value : undefined,
      },
      {
        sortBy: sortByColumn.value ?? undefined,
        sortDirection: (sortDirection.value as 1 | -1) ?? undefined,
      }
    );
    allItems.value = res.data.items;

    if (res.data?.totalCount) {
      totalCount.value = res.data.totalCount;
    }

    if (page.value > totalPages.value) {
      page.value = Math.max(1, totalPages.value);
    }

    // Clear selection when loading new items
    selectedItems.value = [];
    isSelectAllMode.value = false;
  } catch (error) {
    logger.error(error);
    notificationService.error('Failed to load retention data');
  } finally {
    isLoaded.value = true;
    loader.close();
    loader.$destroy();
  }
};

const handleSearchInput = debounce(async () => {
  const loader = Loading.service({ lock: true, text: 'Searching' });

  page.value = 1;

  try {
    await loadItems();
  } catch (e) {
    await apiService.handleResponseError(e);
  } finally {
    loader.close();
    loader.$destroy();
  }
}, 500);

const handleFilterChange = async () => {
  const loader = Loading.service({ lock: true, text: 'Searching' });

  page.value = 1;
  // Reset selection when filters change
  isSelectAllMode.value = false;
  selectedItems.value = [];
  totalCount.value = 0; // Force refetch of total count

  try {
    await loadItems();
  } catch (e) {
    await apiService.handleResponseError(e);
  } finally {
    loader.close();
    loader.$destroy();
  }
};

const handleUpdatePage = async (p: number) => {
  page.value = p;

  await loadItems();
};

const resolveItemRoute = (item: RetentionItem) => ({
  name: item.type === 'article' ? ARTICLE_EDIT : DRAFT_EDIT,
  params: { id: item.id },
  query: { [ORIGIN_QUERY_PARAM]: RETENTION },
});

const openInNewTab = (item: RetentionItem) => {
  window.open(router.resolve(resolveItemRoute(item)).href, '_blank');
};

const handleClickOnRow = async ({ e, item }: { e?: PointerEvent | MouseEvent; item: RetentionItem }) => {
  e?.preventDefault();
  e?.stopPropagation();

  // Keep the Retention page usable behind the new tab, so no blocking loader on this path
  if (e?.metaKey || e?.ctrlKey || e?.button === 1) {
    openInNewTab(item);
    return;
  }

  const loader = Loading.service({ lock: true, text: 'Opening' });

  try {
    await router.push(resolveItemRoute(item));
  } finally {
    loader.close();
  }
};

const handleSortChange = async (e: { sortBy: string[] }) => {
  if (e.sortBy && e.sortBy.length > 0) {
    const column = e.sortBy[0];

    if (sortByColumn.value === column) {
      sortDirection.value *= -1;
    } else {
      sortByColumn.value = column;
      sortDirection.value = column === 'latestVideoCreatedAt' ? -1 : 1;
    }

    // Global sorting requires a refetch from page 1
    page.value = 1;
    await loadItems();
  }
};

const showRetainConfirm = () => {
  if (selectedItems.value.length === 0) return;

  const items = selectedItems.value
    .map((id) => allItems.value.find((i) => i.id === id))
    .filter((item): item is RetentionItem => !!item && !isRetained(item));

  if (items.length === 0) {
    notificationService.warning('All selected items are already retained');
    return;
  }

  pendingRetainItems.value = items;
  retainConfirmMessage.value = `Are you sure you want to mark ${items.length} recording${
    items.length !== 1 ? 's' : ''
  } for long-term retention? These items will be preserved and won't be automatically deleted.`;
  showRetainModal.value = true;
};

const handleConfirmRetain = async () => {
  if (pendingRetainItems.value.length === 0) return;

  isLoading.value = true;
  const loader = Loading.service({ lock: true, text: 'Updating status' });

  try {
    const promises = pendingRetainItems.value.map((item) => {
      return apiService.retention.updateRetentionStatus(item.id, item.type, true);
    });

    await Promise.all(promises);
    await dispatchGetRetentionCount();
    notificationService.success('Items marked for long-term retention');
    showRetainModal.value = false;
    selectedItems.value = [];
    isSelectAllMode.value = false;
    await loadItems();
  } catch (error) {
    logger.error(error);
    notificationService.error('Failed to update retention status');
  } finally {
    isLoading.value = false;
    loader.close();
    loader.$destroy();
  }
};

const showRetainConfirmSingle = (item: RetentionItem) => {
  if (isRetained(item)) {
    notificationService.warning('This item is already retained');
    return;
  }

  pendingRetainItems.value = [item];
  retainConfirmMessage.value = `Are you sure you want to mark "${item.title}" for long-term retention? This item will be preserved and won't be automatically deleted.`;
  showRetainModal.value = true;
};

const showDeleteConfirm = () => {
  if (displaySelectedCount.value === 0) return;

  deleteConfirmMessage.value = `Are you sure you want to delete ${displaySelectedCount.value} item${
    displaySelectedCount.value !== 1 ? 's' : ''
  }? This will permanently remove recordings from these articles.`;

  pendingDeleteIds.value = [...selectedItems.value];
  showDeleteModal.value = true;
};

const handleDeleteSingle = (item: RetentionItem) => {
  deleteConfirmMessage.value = `Are you sure you want to delete "${item.title}"? This will permanently remove recordings from this article.`;

  pendingDeleteIds.value = [item.id];
  showDeleteModal.value = true;
};

const handleConfirmDelete = async () => {
  if (pendingDeleteIds.value.length === 0) return;

  isLoading.value = true;
  const loader = Loading.service({ lock: true, text: 'Processing' });

  try {
    await apiService.retention.bulkDeleteContent(pendingDeleteIds.value);
    await dispatchGetRetentionCount();

    notificationService.success('Recordings deleted successfully');
    showDeleteModal.value = false;

    // Reset selection state
    selectedItems.value = [];
    isSelectAllMode.value = false;
    totalCount.value = 0;

    await loadItems();
  } catch (error) {
    logger.error(error);
    notificationService.error('Failed to delete');
  } finally {
    isLoading.value = false;
    loader.close();
    loader.$destroy();
  }
};

onMounted(() => {
  if (!isRetentionEnabled.value) {
    router.push({ name: HOME });
    return;
  }

  loadFilterData();
  loadItems();
  // Recordings can land here from elsewhere (moved out of a long-term workspace) — keep the badge in step (SNAP-2438)
  dispatchGetRetentionCount();
});

watch([searchWorkspace, searchAuthor, searchStatus], async () => {
  await handleFilterChange();
});

return { __sfc: true,isRetentionEnabled, allItems, isLoaded, isLoading, selectedItems, isSelectAllMode, page, itemPerPage, totalCount, sortByColumn, sortDirection, searchQuery, searchWorkspace, searchAuthor, searchStatus, workspaces, authors, showDeleteModal, deleteConfirmMessage, pendingDeleteIds, showRetainModal, retainConfirmMessage, pendingRetainItems, statusOptions, tableColumns, totalPages, sortedItems, displaySelectedCount, hasNonRetainedSelected, expiredSelectedCount, workspaceTitle, retentionStatus, isRetained, calculateAutoDeleteAt, calculateDaysLeft, formatCreationDate, handleSingleSelection, handleSelectAllToggle, loadFilterData, loadItems, handleSearchInput, handleFilterChange, handleUpdatePage, resolveItemRoute, openInNewTab, handleClickOnRow, handleSortChange, showRetainConfirm, handleConfirmRetain, showRetainConfirmSingle, showDeleteConfirm, handleDeleteSingle, handleConfirmDelete, MyTable, MyTableCell, RETENTION_STATUS_HINTS, RETENTION_STATUS_LABELS, RetentionStatus }
}

})